import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Link } from "react-router-dom";
import { Plus, Search, User } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Collaborators() {
  const [collabs, setCollabs] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [q, setQ] = useState("");
  const [groupTab, setGroupTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "", group_ids: [], with_login: false, login_password: "", is_admin: false });

  const load = async () => {
    const [c, g, t] = await Promise.all([
      api.get("/collaborators"),
      api.get("/groups"),
      api.get("/tasks", { params: { status: "open" } }),
    ]);
    setCollabs(c.data); setGroups(g.data); setTasks(t.data);
  };
  useEffect(() => { load(); }, []);

  const countsByCollab = useMemo(() => {
    const m = {};
    tasks.forEach((t) => {
      m[t.collaborator_id] = m[t.collaborator_id] || { to_me: 0, to_them: 0 };
      m[t.collaborator_id][t.direction] += 1;
    });
    return m;
  }, [tasks]);

  const filtered = useMemo(() => {
    let list = collabs;
    if (groupTab !== "all") list = list.filter((c) => (c.group_ids || []).includes(groupTab));
    if (q.trim()) list = list.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));
    return list;
  }, [collabs, groupTab, q]);

  const toggleGroup = (gid) => {
    setForm((f) => {
      const has = f.group_ids.includes(gid);
      return { ...f, group_ids: has ? f.group_ids.filter((x) => x !== gid) : [...f.group_ids, gid] };
    });
  };

  const create = async () => {
    if (!form.name.trim()) return toast.error("Ime je obavezno");
    if (form.with_login) {
      if (!form.email.trim()) return toast.error("Email je obavezan za login nalog");
      if (!form.login_password || form.login_password.length < 6) return toast.error("Lozinka mora imati bar 6 karaktera");
    }
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email || null,
        role: form.role || null,
        group_ids: form.group_ids,
        login_password: form.with_login ? form.login_password : null,
        is_admin: form.with_login ? !!form.is_admin : false,
      };
      await api.post("/collaborators", payload);
      toast.success(form.with_login ? "Saradnik + login nalog kreiran" : "Saradnik dodat");
      setForm({ name: "", email: "", role: "", group_ids: [], with_login: false, login_password: "", is_admin: false });
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const groupName = (id) => groups.find((g) => g.id === id)?.name;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="overline">Ljudi</div>
          <h1 className="mt-1 text-3xl md:text-4xl font-heading font-semibold tracking-tight">Saradnici</h1>
          <p className="text-ink-muted mt-2">Pretraži i otvori profil saradnika da vidiš odvojene tabele obaveza.</p>
        </div>
        <button className="btn-primary" onClick={() => setOpen(true)} data-testid="new-collab-btn">
          <Plus className="w-4 h-4" /> Novi saradnik
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-ink-muted absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="collab-search"
            placeholder="Pretraži po imenu..."
            className="w-full pl-9 pr-3 py-2.5 bg-white border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-forest"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <Tabs value={groupTab} onValueChange={setGroupTab} className="w-full md:w-auto overflow-x-auto">
          <TabsList className="bg-surface-secondary">
            <TabsTrigger value="all" data-testid="tab-group-all">Sve</TabsTrigger>
            {groups.map((g) => (
              <TabsTrigger key={g.id} value={g.id} data-testid={`tab-group-${g.id}`}>{g.name}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((c) => {
          const cnt = countsByCollab[c.id] || { to_me: 0, to_them: 0 };
          return (
              <Link
                to={`/saradnici/${c.id}`}
                key={c.id}
                data-testid={`collab-card-${c.id}`}
                className="surface-card p-5 hover:bg-surface-secondary transition-colors block"
              >
              <div className="flex items-center gap-3">
                {c.avatar_url ? (
                  <img src={c.avatar_url} alt={c.name} className="w-12 h-12 rounded-md object-cover border border-line" />
                ) : (
                  <div className="w-12 h-12 rounded-md bg-surface-secondary border border-line grid place-items-center text-ink-muted">
                    <User className="w-5 h-5" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-heading font-semibold truncate flex items-center gap-1.5">
                    {c.name}
                    {c.linked_user_id && (
                      <span
                        data-testid={`collab-has-login-${c.id}`}
                        title="Saradnik ima login nalog"
                        className="text-[10px] uppercase tracking-widest font-bold text-forest bg-forest/10 border border-forest/30 px-1.5 py-0.5 rounded"
                      >
                        login
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-ink-muted truncate">{c.role || c.email || "—"}</div>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {(c.group_ids || []).map((gid) => (
                  <span key={gid} className="text-xs px-2 py-0.5 rounded bg-surface-secondary border border-line text-ink-muted">
                    {groupName(gid) || "grupa"}
                  </span>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-line grid grid-cols-2 gap-2 text-xs">
                <div><div className="text-ink-muted">On duguje meni</div><div className="font-heading text-lg text-forest">{cnt.to_me}</div></div>
                <div><div className="text-ink-muted">Ja dugujem</div><div className="font-heading text-lg text-terracotta">{cnt.to_them}</div></div>
              </div>
            </Link>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full surface-card p-8 text-center text-ink-muted">Nema saradnika u ovom filteru.</div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="collab-dialog" className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Novi saradnik</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Ime i prezime</Label><Input data-testid="collab-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Email (opcionalno)</Label><Input data-testid="collab-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Uloga (opcionalno)</Label><Input data-testid="collab-role-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="npr. Video produkcija" /></div>
            <div>
              <Label>Grupacije</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {groups.map((g) => {
                  const on = form.group_ids.includes(g.id);
                  return (
                    <button
                      key={g.id}
                      type="button"
                      data-testid={`collab-group-chip-${g.id}`}
                      onClick={() => toggleGroup(g.id)}
                      className={`text-xs px-2.5 py-1 rounded border transition-colors ${on ? "bg-forest text-white border-forest" : "bg-white border-line text-ink hover:bg-surface-secondary"}`}
                    >
                      {g.name}
                    </button>
                  );
                })}
                {groups.length === 0 && <div className="text-xs text-ink-muted">Prvo dodaj grupacije.</div>}
              </div>
            </div>

            <div className="pt-2 border-t border-line">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  data-testid="collab-with-login"
                  type="checkbox"
                  checked={form.with_login}
                  onChange={(e) => setForm({ ...form, with_login: e.target.checked })}
                />
                Napravi mu login nalog (saradnik će moći da se prijavi)
              </label>
              {form.with_login && (
                <div className="mt-3 space-y-3 pl-6">
                  <div>
                    <Label>Lozinka za saradnika (min. 6)</Label>
                    <Input
                      data-testid="collab-login-password"
                      type="text"
                      value={form.login_password}
                      onChange={(e) => setForm({ ...form, login_password: e.target.value })}
                      placeholder="npr. Voden123!"
                    />
                    <div className="text-xs text-ink-muted mt-1">Pošalji ovu lozinku i email na koji si upisala saradniku, on može posle promeniti u Profilu.</div>
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      data-testid="collab-is-admin"
                      type="checkbox"
                      checked={form.is_admin}
                      onChange={(e) => setForm({ ...form, is_admin: e.target.checked })}
                    />
                    Postavi kao administratora
                  </label>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <button className="btn-ghost" onClick={() => setOpen(false)}>Otkaži</button>
            <button className="btn-primary" onClick={create} data-testid="collab-save-btn">Sačuvaj</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
